/**
 * Candidate Vault — redesigned per /app/design_guidelines.json.
 *
 * Banking-default tab manifest (10 tabs) sourced from a shared config so the
 * platform can be repurposed for healthcare / legal etc. without touching the
 * visual chrome.
 *
 * Persona-aware redaction: same component serves both candidate-self and
 * recruiter views; recruiter view hides DOB, NI, full address with a frosted
 * blur overlay (reveal-on-click is audit-logged via the existing audit pipeline).
 */
import React, { useMemo, useState } from 'react';
import {
  ShieldCheck, FileBadge2, BriefcaseBusiness, GraduationCap, ScanSearch,
  Scale, ScrollText, Stethoscope, History, Lock,
} from 'lucide-react';

/* =========================================================================
   Banking-default tab manifest.
   Schema is platform-portable: swap this constant for a tenant's industry
   preset (healthcare, legal, etc) without touching the rest of the page.
   ========================================================================= */
export const BANKING_VAULT_TABS = [
  {
    id: 'identity', label: 'Identity', Icon: ShieldCheck,
    legal_basis: 'UK GDPR Art. 6(1)(c) + Immigration Act 2016',
    description: 'Government-issued ID, proof of address, NI number.',
    required: true,
  },
  {
    id: 'rtw', label: 'Right to Work', Icon: FileBadge2,
    legal_basis: 'UK Immigration Act 2016 + Home Office Code of Practice',
    description: 'UK share code, visa, BRP — with expiry alerts.',
    required: true,
  },
  {
    id: 'employment_history', label: 'Employment history', Icon: BriefcaseBusiness,
    legal_basis: 'FCA SYSC 22, BIPRU 2.5',
    description: '5-year (10-year for SMCR senior) employment + reference history.',
    required: true,
  },
  {
    id: 'qualifications', label: 'Qualifications', Icon: GraduationCap,
    legal_basis: 'FCA TC sourcebook',
    description: 'Degrees, regulatory certifications, professional memberships.',
    required: true,
  },
  {
    id: 'background_checks', label: 'Background checks', Icon: ScanSearch,
    legal_basis: 'SYSC 22, JMLSG guidance',
    description: 'DBS, CIFAS, credit, sanctions, adverse media — consent-gated.',
    required: true,
  },
  {
    id: 'smcr', label: 'Regulatory references (SMCR)', Icon: Scale,
    legal_basis: 'FCA/PRA Joint Reference Template',
    description: '6-year structured regulatory ref for senior managers & certification staff.',
    required: true,
  },
  {
    id: 'conduct', label: 'Conduct & conflicts', Icon: ScrollText,
    legal_basis: 'FCA SYSC 28 (COCON)',
    description: 'Outside business interests, conflicts of interest, political exposure.',
    required: true,
  },
  {
    id: 'acknowledgements', label: 'Acknowledgements', Icon: ScrollText,
    legal_basis: 'SYSC 28.2',
    description: 'Code of conduct, anti-bribery, market abuse policy acceptances.',
    required: true,
  },
  {
    id: 'health_fitness', label: 'Health & fitness', Icon: Stethoscope,
    legal_basis: 'Equality Act 2010 — role-conditional only',
    description: 'Occupational health attestation. Only visible when the target role requires it.',
    required: false,
  },
  {
    id: 'audit_trail', label: 'Audit trail', Icon: History,
    legal_basis: 'UK GDPR Art. 15, FCA record-keeping',
    description: 'Every view of every field, logged.',
    required: true,
  },
];

/* =========================================================================
   CompletionRing — accessible circular progress.
   ========================================================================= */
function CompletionRing({ value, size = 24, stroke = 3 }) {
  const v = Math.max(0, Math.min(100, value || 0));
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (v / 100) * circ;
  const colour = v >= 100 ? '#4CAF50' : v >= 50 ? '#C9A847' : '#E06D53';

  return (
    <span className="relative inline-flex items-center justify-center" aria-hidden>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#E5DFD5" strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          stroke={colour} strokeWidth={stroke} fill="none"
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <span className="vh-sr-only">{v}% complete</span>
    </span>
  );
}

/* =========================================================================
   RedactableField — frosted-blur reveal pattern for the recruiter persona.
   ========================================================================= */
function RedactableField({ label, value, persona, sensitive = false, onReveal }) {
  const isRecruiter = persona === 'recruiter';
  const [revealed, setRevealed] = useState(false);
  const shouldBlur = isRecruiter && sensitive && !revealed;

  const handleReveal = (e) => {
    e.preventDefault();
    if (!shouldBlur) return;
    setRevealed(true);
    if (onReveal) onReveal(label);
  };

  return (
    <div className="text-sm" data-testid={`vault-field-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <p className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[var(--vh-text-muted)] mb-0.5">
        {label}
        {sensitive && isRecruiter && (
          <span className="ml-2 inline-flex items-center gap-1 normal-case tracking-normal text-[10px] text-[var(--vh-accent)]">
            <Lock className="w-2.5 h-2.5" /> Sensitive
          </span>
        )}
      </p>
      <button
        type="button"
        onClick={handleReveal}
        disabled={!shouldBlur}
        aria-label={shouldBlur ? `Reveal ${label} — this action is audit logged` : label}
        className={`text-[var(--vh-text-primary)] font-medium block text-left w-full ${shouldBlur ? 'filter blur-sm select-none hover:blur-none cursor-help' : 'cursor-default'}`}
        data-testid={`vault-field-value-${label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        {value || <span className="text-[var(--vh-text-muted)] italic">Not provided</span>}
      </button>
    </div>
  );
}

/* =========================================================================
   VaultIdentityCard — top-left identity tile.
   ========================================================================= */
export function VaultIdentityCard({ candidate, persona = 'self', onRevealField }) {
  const initials = (candidate?.full_name || '?').split(/\s+/).map((p) => p[0]).join('').slice(0, 2).toUpperCase();
  return (
    <section
      data-testid="vault-identity-card"
      className="bg-white border border-[var(--vh-border)] rounded-2xl shadow-[0_4px_24px_rgba(30,63,51,0.04)] p-6 flex flex-col sm:flex-row gap-5 items-start"
    >
      <div className="w-16 h-16 shrink-0 rounded-2xl bg-[var(--vh-brand-primary)] text-white text-xl font-display font-bold flex items-center justify-center" aria-hidden>
        {initials}
      </div>
      <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 w-full">
        <div className="sm:col-span-2">
          <p className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[var(--vh-text-muted)]">Candidate</p>
          <h2 className="font-display font-bold text-2xl tracking-tight leading-tight">{candidate?.full_name || 'Anonymous candidate'}</h2>
          {candidate?.target_role && <p className="text-sm text-[var(--vh-text-secondary)] mt-0.5">Target role: <strong>{candidate.target_role}</strong></p>}
        </div>
        <RedactableField label="Email"      value={candidate?.email}      persona={persona} />
        <RedactableField label="Phone"      value={candidate?.phone}      persona={persona} sensitive onReveal={onRevealField} />
        <RedactableField label="Date of birth" value={candidate?.dob}     persona={persona} sensitive onReveal={onRevealField} />
        <RedactableField label="NI number"  value={candidate?.ni_number}  persona={persona} sensitive onReveal={onRevealField} />
        <RedactableField label="Address"    value={candidate?.address}    persona={persona} sensitive onReveal={onRevealField} />
        <RedactableField label="City"       value={candidate?.city}       persona={persona} />
      </div>
    </section>
  );
}

/* =========================================================================
   VaultTabs — horizontal tab strip with completion rings.
   ========================================================================= */
export function VaultTabs({ tabs, completion, activeId, onSelect }) {
  return (
    <div role="tablist" aria-label="Compliance pack sections" data-testid="vault-tabs"
         className="w-full overflow-x-auto -mx-2 px-2 pb-2">
      <div className="inline-flex gap-1 bg-[var(--vh-bg-muted)] p-1 rounded-2xl">
        {tabs.map((t) => {
          const Icon = t.Icon;
          const v = completion[t.id] || 0;
          const active = t.id === activeId;
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={active}
              aria-controls={`vault-panel-${t.id}`}
              id={`vault-tab-${t.id}`}
              data-testid={`vault-tab-${t.id}`}
              onClick={() => onSelect(t.id)}
              className={`whitespace-nowrap inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm transition-colors ${active ? 'bg-white text-[var(--vh-brand-primary)] shadow-sm font-medium' : 'text-[var(--vh-text-secondary)] hover:text-[var(--vh-text-primary)]'}`}
            >
              <Icon className="w-4 h-4" aria-hidden />
              <span>{t.label}</span>
              <CompletionRing value={v} size={18} stroke={2.5} />
              <span className="vh-sr-only">— {v}% complete</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* =========================================================================
   VaultTabContent — uniform per-tab content shell. The actual content
   (uploaded documents, fields, etc) is provided by the caller; this just
   gives a consistent header pattern.
   ========================================================================= */
export function VaultTabContent({ tab, completion, children }) {
  if (!tab) return null;
  const Icon = tab.Icon;
  const v = completion[tab.id] || 0;
  const status = v >= 100 ? 'Complete' : v >= 50 ? 'In progress' : 'Needs attention';
  const statusColor = v >= 100
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : v >= 50
      ? 'bg-amber-50 text-amber-700 border-amber-200'
      : 'bg-rose-50 text-rose-700 border-rose-200';
  return (
    <section
      role="tabpanel"
      id={`vault-panel-${tab.id}`}
      aria-labelledby={`vault-tab-${tab.id}`}
      data-testid={`vault-panel-${tab.id}`}
      className="bg-white border border-[var(--vh-border)] rounded-2xl p-6 shadow-[0_4px_24px_rgba(30,63,51,0.04)]"
    >
      <header className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-[var(--vh-bg-muted)] text-[var(--vh-brand-primary)] flex items-center justify-center shrink-0" aria-hidden>
            <Icon className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h3 className="font-display font-semibold text-lg leading-tight">{tab.label}</h3>
            <p className="text-xs text-[var(--vh-text-muted)] mt-0.5">{tab.description}</p>
            {tab.legal_basis && (
              <p className="text-[11px] text-[var(--vh-text-muted)] mt-1">Legal basis: {tab.legal_basis}</p>
            )}
          </div>
        </div>
        <span className={`text-xs px-2.5 py-1 rounded-full border ${statusColor}`} data-testid={`vault-status-${tab.id}`}>
          {status} · {v}%
        </span>
      </header>
      <div>{children}</div>
    </section>
  );
}

/* =========================================================================
   CandidateVault — main composer for both candidate and recruiter personas.
   ========================================================================= */
export default function CandidateVault({
  candidate,
  persona = 'self',
  completion = {},
  tabs = BANKING_VAULT_TABS,
  renderTab,
  onRevealField,
}) {
  // Filter health/fitness when role doesn't require it (self view only sees it if required)
  const visibleTabs = useMemo(
    () => tabs.filter((t) => t.required || candidate?.required_tabs?.includes(t.id)),
    [tabs, candidate]
  );
  const [activeId, setActiveId] = useState(visibleTabs[0]?.id);
  const activeTab = visibleTabs.find((t) => t.id === activeId);

  // Aggregate compliance pack readiness
  const overall = useMemo(() => {
    if (visibleTabs.length === 0) return 0;
    const total = visibleTabs.reduce((sum, t) => sum + (completion[t.id] || 0), 0);
    return Math.round(total / visibleTabs.length);
  }, [visibleTabs, completion]);

  return (
    <div className="px-6 lg:px-10 py-8 space-y-6" data-testid="candidate-vault">
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <VaultIdentityCard candidate={candidate} persona={persona} onRevealField={onRevealField} />
        </div>
        <div className="bg-[var(--vh-brand-primary)] text-white rounded-2xl p-6 shadow-[0_8px_32px_rgba(30,63,51,0.08)]">
          <p className="text-[11px] uppercase tracking-[0.2em] font-semibold opacity-70">Compliance pack readiness</p>
          <div className="mt-3 flex items-end gap-3">
            <p className="font-display font-bold text-5xl tracking-tight leading-none">{overall}%</p>
            <p className="pb-1 text-sm opacity-90">{overall >= 100 ? 'Ready to submit' : overall >= 50 ? 'In progress' : 'Just getting started'}</p>
          </div>
          <p className="text-xs opacity-80 mt-3">Against the requirements for your target role. Complete every section to unlock submission.</p>
        </div>
      </div>

      <VaultTabs tabs={visibleTabs} completion={completion} activeId={activeId} onSelect={setActiveId} />

      <VaultTabContent tab={activeTab} completion={completion}>
        {renderTab ? renderTab(activeTab) : (
          <div className="py-10 text-center text-sm text-[var(--vh-text-muted)]" data-testid="vault-placeholder">
            <p>Content for "{activeTab?.label}" will appear here.</p>
            <p className="mt-1 text-[11px]">Upload your documents or complete the form for this section.</p>
          </div>
        )}
      </VaultTabContent>
    </div>
  );
}
